import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';

const TeacherAbsenceManager = ({ teacher }) => {
  const [view, setView] = useState('seances'); // seances, appel, historique
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [absentIds, setAbsentIds] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [filters, setFilters] = useState({ academicYear: '', semester: 1 });
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const apiUrl = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    if (teacher?._id) {
      loadAcademicYears();
      loadTeacherCourses();
    }
  }, [teacher]);

  useEffect(() => {
    if (filters.academicYear && filters.semester) {
      loadSessions();
    }
  }, [filters]);

  const loadAcademicYears = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/academic-years`);
      setAcademicYears(res.data);
      if (res.data.length > 0) {
        setFilters({ ...filters, academicYear: res.data[0]._id });
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const loadTeacherCourses = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.academicYear) params.append('academicYear', filters.academicYear);
      if (filters.semester) params.append('semester', filters.semester);
      
      const res = await axios.get(`${apiUrl}/api/teacher-space/teacher-courses/${teacher._id}?${params}`);
      setCourses(res.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  useEffect(() => {
    if (teacher?._id && filters.academicYear && filters.semester) {
      loadTeacherCourses();
    }
  }, [filters.academicYear, filters.semester]);

  const loadSessions = async () => {
    try {
      const params = new URLSearchParams();
      params.append('academicYear', filters.academicYear);
      params.append('semester', filters.semester);
      
      const res = await axios.get(`${apiUrl}/api/teacher-space/schedule/${teacher._id}?${params}`);
      setSessions(res.data.sessions || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const loadStudents = async (sessionId, date) => {
    try {
      const res = await axios.get(`${apiUrl}/api/teacher-space/session/${sessionId}`);
      const session = res.data;
      
      const classId = session.class?._id || session.class;
      const academicYearId = session.class?.academicYear?._id || session.class?.academicYear || session.academicYear;
      
      console.log('👥 Loading students:', { classId, academicYearId, date });
      
      // Récupérer les étudiants de la classe
      const studentsRes = await axios.get(`${apiUrl}/api/teacher-space/students/${classId}?academicYear=${academicYearId}`);
      console.log('✅ Students loaded:', studentsRes.data.length);
      setStudents(studentsRes.data || []);
      
      // Récupérer les absences pour cette séance ET cette date
      const absencesRes = await axios.get(`${apiUrl}/api/teacher-space/absences/${sessionId}?date=${date}`);
      const existingAbsences = absencesRes.data || [];
      setAbsentIds(existingAbsences.map(a => a.student._id || a.student));
    } catch (error) {
      console.error('❌ Erreur loadStudents:', error);
      setMessage('Erreur lors du chargement des étudiants');
    }
  };

  const loadAbsenceHistory = async () => {
    if (!selectedCourse) return;
    try {
      const res = await axios.get(`${apiUrl}/api/teacher-space/absences-history/${selectedCourse}?academicYear=${filters.academicYear}&semester=${filters.semester}`);
      setAbsences(res.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleSessionSelect = (session) => {
    setSelectedSession(session);
    setSelectedDate(new Date().toISOString().split('T')[0]);
    loadStudents(session._id, new Date().toISOString().split('T')[0]);
    setView('appel');
  };

  const toggleStudent = (studentId) => {
    setAbsentIds(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const saveAbsences = async () => {
    if (!selectedSession || !selectedDate) return;
    
    setLoading(true);
    try {
      console.log('💾 Saving absences:', {
        sessionId: selectedSession._id,
        date: selectedDate,
        absentCount: absentIds.length
      });
      
      const response = await axios.post(`${apiUrl}/api/teacher-space/absences/bulk`, {
        sessionId: selectedSession._id,
        absentStudentIds: absentIds,
        date: selectedDate,
        notes: `Appel du ${new Date(selectedDate).toLocaleDateString()}`
      });
      
      console.log('✅ Response:', response.data);
      
      setMessage(`${absentIds.length} absence(s) enregistrée(s) pour le ${new Date(selectedDate).toLocaleDateString()}`);
      setTimeout(() => {
        setMessage('');
        setView('seances');
      }, 2000);
    } catch (error) {
      console.error('❌ Erreur save:', error.response?.data || error);
      setMessage(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  // Vue Séances
  const renderSeances = () => {
    const groupedSessions = sessions.reduce((acc, session) => {
      const courseId = session.course?._id;
      if (!acc[courseId]) {
        acc[courseId] = {
          course: session.course,
          sessions: []
        };
      }
      acc[courseId].sessions.push(session);
      return acc;
    }, {});

    return (
      <div className="space-y-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select 
              value={filters.academicYear} 
              onChange={(e) => setFilters({...filters, academicYear: e.target.value})}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">Année universitaire</option>
              {academicYears.map(year => (
                <option key={year._id} value={year._id}>{year.name}</option>
              ))}
            </select>
            
            <select 
              value={filters.semester} 
              onChange={(e) => setFilters({...filters, semester: parseInt(e.target.value)})}
              className="px-3 py-2 border rounded-lg"
            >
              <option value={1}>Semestre 1</option>
              <option value={2}>Semestre 2</option>
            </select>
          </div>
        </div>

        {/* Liste des séances par matière */}
        {Object.values(groupedSessions).map(({ course, sessions }) => (
          <div key={course._id} className="bg-white rounded-lg shadow">
            <div className="bg-blue-50 px-4 py-3 border-b">
              <h3 className="font-semibold text-blue-900">{course.name}</h3>
              <p className="text-sm text-blue-700">{sessions.length} séance(s)</p>
            </div>
            <div className="divide-y">
              {sessions.map(session => (
                <div key={session._id} className="p-4 hover:bg-gray-50 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{session.class?.name}</div>
                    <div className="text-sm text-gray-600">
                      {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'][session.dayOfWeek - 1]} - 
                      Créneau {session.timeSlot} - {session.room?.name}
                      {session.group && ` - ${session.group}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleSessionSelect(session)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Faire l'appel
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {sessions.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-4xl mb-4">📅</div>
            <p className="text-gray-600">Aucune séance trouvée</p>
          </div>
        )}
      </div>
    );
  };

  // Vue Appel
  const renderAppel = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">{selectedSession?.course?.name}</h3>
            <p className="text-sm text-gray-600">
              {selectedSession?.class?.name} - {selectedSession?.room?.name}
            </p>
          </div>
          <button
            onClick={() => setView('seances')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Retour
          </button>
        </div>

        {/* Sélecteur de date */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date de la séance
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              loadStudents(selectedSession._id, e.target.value);
            }}
            className="px-3 py-2 border rounded-lg"
          />
          <p className="text-xs text-gray-500 mt-1">
            Changez la date pour faire l'appel d'une autre occurrence de cette séance
          </p>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded ${
            message.includes('Erreur') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}>
            {message}
          </div>
        )}

        {/* Statistiques */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-900">{students.length}</div>
            <div className="text-sm text-blue-700">Total</div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-900">{students.length - absentIds.length}</div>
            <div className="text-sm text-green-700">Présents</div>
          </div>
          <div className="bg-red-50 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-red-900">{absentIds.length}</div>
            <div className="text-sm text-red-700">Absents</div>
          </div>
        </div>

        {/* Liste des étudiants */}
        {students.length === 0 ? (
          <div className="text-center py-8 mb-6">
            <div className="text-4xl mb-2">👥</div>
            <p className="text-gray-600">Aucun étudiant trouvé dans cette classe</p>
            <p className="text-sm text-gray-500 mt-2">Vérifiez que des étudiants sont inscrits dans cette classe</p>
          </div>
        ) : (
          <div className="space-y-2 mb-6">
            {students.map(student => (
              <label
                key={student._id}
                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  absentIds.includes(student._id) 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-green-300 bg-green-50 hover:bg-green-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={absentIds.includes(student._id)}
                  onChange={() => toggleStudent(student._id)}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium">
                    {student.firstName} {student.lastName}
                  </div>
                  <div className="text-sm text-gray-600">
                    {student.studentCardNumber || student.nationalIdNumber}
                  </div>
                </div>
                <span className={`font-medium ${
                  absentIds.includes(student._id) ? 'text-red-600' : 'text-green-600'
                }`}>
                  {absentIds.includes(student._id) ? 'Absent' : 'Présent'}
                </span>
              </label>
            ))}
          </div>
        )}

        {/* Bouton de sauvegarde */}
        <button
          onClick={saveAbsences}
          disabled={loading}
          className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
        >
          {loading ? 'Enregistrement...' : `Enregistrer l'appel (${absentIds.length} absent(s))`}
        </button>
      </div>
    </div>
  );

  // Charger l'historique quand le cours change
  useEffect(() => {
    if (view === 'historique' && selectedCourse) {
      loadAbsenceHistory();
    }
  }, [selectedCourse, view]);

  // Vue Historique
  const renderHistorique = () => {
    const groupedAbsences = absences.reduce((acc, absence) => {
      const studentId = absence.student?._id || absence.student;
      if (!acc[studentId]) {
        acc[studentId] = {
          student: absence.student,
          absences: []
        };
      }
      acc[studentId].absences.push(absence);
      return acc;
    }, {});

    return (
      <div className="space-y-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select 
              value={filters.academicYear} 
              onChange={(e) => setFilters({...filters, academicYear: e.target.value})}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">Année universitaire</option>
              {academicYears.map(year => (
                <option key={year._id} value={year._id}>{year.name}</option>
              ))}
            </select>
            
            <select 
              value={filters.semester} 
              onChange={(e) => setFilters({...filters, semester: parseInt(e.target.value)})}
              className="px-3 py-2 border rounded-lg"
            >
              <option value={1}>Semestre 1</option>
              <option value={2}>Semestre 2</option>
            </select>

            <select 
              value={selectedCourse} 
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">Sélectionner une matière</option>
              {courses.map(course => (
                <option key={course._id} value={course._id}>{course.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Historique des absences */}
        {selectedCourse ? (
          <div className="bg-white rounded-lg shadow">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-semibold">Historique des absences</h3>
              <p className="text-sm text-gray-600">{absences.length} absence(s) enregistrée(s)</p>
            </div>
            <div className="divide-y">
              {Object.values(groupedAbsences).map(({ student, absences }) => (
                <div key={student._id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium">{student.firstName} {student.lastName}</div>
                      <div className="text-sm text-gray-600">{student.studentCardNumber}</div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      absences.length >= 3 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {absences.length} absence(s)
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    {absences.map(absence => (
                      <div key={absence._id}>
                        • {new Date(absence.date).toLocaleDateString()} 
                        {absence.isJustified && ' (Justifiée)'}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-4xl mb-4">📊</div>
            <p className="text-gray-600">Sélectionnez une matière pour voir l'historique</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Navigation */}
      <div className="mb-6 flex gap-2 overflow-x-auto">
        <button
          onClick={() => setView('seances')}
          className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
            view === 'seances' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          📅 Mes Séances
        </button>
        <button
          onClick={() => setView('historique')}
          className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
            view === 'historique' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          📊 Historique
        </button>
      </div>

      {/* Contenu */}
      {view === 'seances' && renderSeances()}
      {view === 'appel' && renderAppel()}
      {view === 'historique' && renderHistorique()}
    </div>
  );
};

export default TeacherAbsenceManager;